import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { axiosInstance } from "@/lib/api";
import { queue } from "@/components/Toast";
import type {
  AcceptIdeaResult,
  CalendarIdeaStatus,
  CalendarPlanSlot,
  CalendarSlotIdea,
  IdeaMediaPick,
  IdeaEditInput,
  IdeaReference,
  ManualIdeaInput,
  ReviewIdeaSnapshot,
} from "@/types/contentCalendar.type";
import {
  cancelPlanFetches,
  patchIdea,
  patchSlot,
  planWriteKey,
  putSlot,
  readIdea,
  readSlot,
  settlePlanWrite,
} from "./calendarCache";
import { acceptBriefWithPost, planBriefsKey, slotBriefKey } from "./useProductionBrief";
import { conceptVersionsKey } from "./useConceptVersions";

function errorMessage(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    return (err.response?.data as { message?: string })?.message ?? err.message ?? fallback;
  }
  return fallback;
}

interface SlotResponse {
  slot: CalendarPlanSlot;
}

function ideaFromDeclineSnapshot(slot: CalendarPlanSlot): CalendarSlotIdea[] {
  const snapshot: ReviewIdeaSnapshot | undefined = slot.reviewDecisions?.[0]?.ideaSnapshot;
  if (!snapshot?.id || !snapshot.title) return [];
  const now = new Date().toISOString();
  return [
    {
      id: snapshot.id,
      slotId: slot.id,
      title: snapshot.title,
      synopsis: snapshot.synopsis,
      hooks: snapshot.hooks ?? [],
      objectiveClaim: snapshot.objectiveClaim,
      channels: snapshot.channels,
      aspectRatio: snapshot.aspectRatio,
      durationSeconds: snapshot.durationSeconds,
      reference: null,
      sources: [],
      status: "PROPOSED",
      order: 0,
      decidedByUserId: null,
      decidedAt: null,
      createdAt: now,
      updatedAt: now,
    },
  ];
}

/** Undo for a seat-level action: the slot exactly as it was before the click. */
interface SlotUndo {
  slot?: CalendarPlanSlot;
}

/**
 * Undo for an idea-level action: only that idea's status. Restoring the whole
 * slot here would also revert a sibling idea being reworked at the same time.
 */
interface IdeaUndo {
  slotId: string;
  ideaId: string;
  status?: CalendarIdeaStatus;
}

/**
 * The ideation actions on one slot.
 *
 * All are fire-and-forget on the backend (202), but the claim is already
 * committed when the call returns and the response carries the updated slot. So
 * each action moves the cache twice: once on the click (the move the server is
 * about to make) and once on the response (the server's own copy, laid over
 * by the month's write gate once no other write is in flight). Nothing waits on
 * a re-fetch, and a failure puts back exactly what it took.
 *
 * The long tail — the agent actually finishing — arrives as a realtime hint
 * (polling while the socket is down).
 */
export function useSlotIdeation(planId: string | null) {
  const qc = useQueryClient();

  const writeKey = (name: string) => [...planWriteKey(planId), name];
  // A refusal is usually a 409 (the seat moved on under us), so the restored
  // view is stale too: the settle refetches it.
  const settle = (slot: CalendarPlanSlot | undefined, err: unknown) =>
    settlePlanWrite(qc, planId, err ? { refetch: true } : { slot });
  const settleResult = (result: AcceptIdeaResult | undefined, err: unknown) =>
    settle(result?.slot, err);

  const reportError = (title: string, err: unknown) => {
    queue.add({
      title,
      description: errorMessage(err, "Please try again."),
      variant: "error",
    });
  };

  const revertSlot =
    (title: string) => (err: unknown, _vars: unknown, undo: SlotUndo | undefined) => {
      if (undo?.slot) putSlot(qc, planId, undo.slot);
      reportError(title, err);
    };

  const revertIdea =
    (title: string) => (err: unknown, _vars: unknown, undo: IdeaUndo | undefined) => {
      const status = undo?.status;
      if (undo && status) {
        patchIdea(qc, planId, undo.slotId, undo.ideaId, (idea) => ({ ...idea, status }));
      }
      reportError(title, err);
    };

  /**
   * `force` is the recovery path for a post frozen on "Researching": it lets the
   * claim be taken from a run past the force floor instead of waiting out the
   * 45-minute stale window. The floor is the server's to enforce — this flag only
   * asks. See slotStage's STUCK_AFTER_MS.
   */
  const generateIdeas = useMutation({
    mutationKey: writeKey("generateIdeas"),
    /**
     * The revision DIRECTION is not sent here. It is saved first
     * (PATCH .../revision-alignment) and the server reads it from the post, so a
     * body field would be a second, unchecked way to steer a run — which is
     * exactly what the alignment checkpoint exists to prevent. Callers still
     * save the direction before calling this; they just do not repeat it.
     */
    mutationFn: async (vars: string | { slotId: string; force?: boolean }) => {
      const { slotId, force } =
        typeof vars === "string" ? { slotId: vars, force: false } : vars;
      const res = await axiosInstance.post<SlotResponse>(
        `/content-calendar/plan-slots/${slotId}/ideas/generate`,
        { force: force ?? false },
      );
      return res.data.slot;
    },
    onMutate: async (vars): Promise<SlotUndo> => {
      const slotId = typeof vars === "string" ? vars : vars.slotId;
      await cancelPlanFetches(qc, planId);
      const slot = readSlot(qc, planId, slotId);
      // The claim clears the old ideas and hands the seat to the agents — show
      // the research run now rather than a beat later. On a forced restart this
      // also resets the clock the stuck banner reads, so the banner clears the
      // moment the new run is claimed.
      patchSlot(qc, planId, slotId, (s) => ({
        ...s,
        status: "GENERATING_IDEAS",
        ideaError: null,
        ideas: [],
        updatedAt: new Date().toISOString(),
      }));
      return { slot };
    },
    onSettled: settle,
    onSuccess: () => {
      queue.add({
        title: "Generating ideas",
        description: "The agent is researching — ideas land on the post in a few minutes.",
        variant: "info",
      });
    },
    onError: revertSlot("Could not start generating ideas"),
  });

  /**
   * Keep the concept the client declined — back on the board so it can be
   * edited by hand or redirected, instead of throwing it away for three new
   * ones. The live idea rows are still there until a regenerate wipes them;
   * the snapshot is the fallback if they are not.
   */
  const selectDeclinedIdea = useMutation({
    mutationKey: writeKey("selectDeclinedIdea"),
    mutationFn: async (slotId: string) => {
      const res = await axiosInstance.post<SlotResponse>(
        `/content-calendar/plan-slots/${slotId}/ideas/select-declined`,
      );
      return res.data.slot;
    },
    onMutate: async (slotId): Promise<SlotUndo> => {
      await cancelPlanFetches(qc, planId);
      const slot = readSlot(qc, planId, slotId);
      patchSlot(qc, planId, slotId, (s) => {
        const live = s.ideas ?? [];
        const ideas =
          live.length > 0
            ? live.map((idea) =>
                idea.status === "ACCEPTED"
                  ? { ...idea, status: "PROPOSED" as const, decidedByUserId: null, decidedAt: null }
                  : idea,
              )
            : ideaFromDeclineSnapshot(s);
        return {
          ...s,
          status: "IDEATION",
          ideaError: null,
          ideas,
          updatedAt: new Date().toISOString(),
        };
      });
      return { slot };
    },
    onSettled: settle,
    onSuccess: () => {
      queue.add({
        title: "Concept selected",
        description: "You can edit it here, or open the workroom to change it in chat.",
        variant: "success",
      });
    },
    onError: revertSlot("Could not select the declined concept"),
  });

  /**
   * Free an idea frozen mid-rework. Non-destructive: the rework never wrote
   * anything, so the card comes back exactly as it was before the click that
   * started it.
   */
  const cancelIdeaRework = useMutation({
    mutationKey: writeKey("cancelIdeaRework"),
    mutationFn: async (payload: { slotId: string; ideaId: string }) => {
      const res = await axiosInstance.post<SlotResponse>(
        `/content-calendar/plan-slots/${payload.slotId}/ideas/${payload.ideaId}/cancel-rework`,
      );
      return res.data.slot;
    },
    onMutate: async ({ slotId, ideaId }): Promise<IdeaUndo> => {
      await cancelPlanFetches(qc, planId);
      const status = readIdea(qc, planId, slotId, ideaId)?.status;
      patchIdea(qc, planId, slotId, ideaId, (idea) => ({ ...idea, status: "PROPOSED" }));
      return { slotId, ideaId, status };
    },
    onSettled: settle,
    onSuccess: () => {
      queue.add({
        title: "Rework cancelled",
        description: "The idea is back as it was. You can approve it or try reworking it again.",
        variant: "info",
      });
    },
    onError: revertIdea("Could not cancel the rework"),
  });

  const regenerateIdea = useMutation({
    mutationKey: writeKey("regenerateIdea"),
    mutationFn: async (payload: { slotId: string; ideaId: string }) => {
      const res = await axiosInstance.post<SlotResponse>(
        `/content-calendar/plan-slots/${payload.slotId}/ideas/${payload.ideaId}/regenerate`,
      );
      return res.data.slot;
    },
    onMutate: async ({ slotId, ideaId }): Promise<IdeaUndo> => {
      await cancelPlanFetches(qc, planId);
      const status = readIdea(qc, planId, slotId, ideaId)?.status;
      // Mirrors claimWorking(): this one card goes to work. Its siblings stay
      // live: a per-idea claim is exactly what the backend enforces. The
      // stamp is the claim's start, which the card's "Rewriting" clock reads.
      patchIdea(qc, planId, slotId, ideaId, (idea) => ({ ...idea, status: "WORKING", updatedAt: new Date().toISOString() }));
      return { slotId, ideaId, status };
    },
    onSettled: settle,
    onError: revertIdea("Could not regenerate the idea"),
  });

  const modifyIdea = useMutation({
    mutationKey: writeKey("modifyIdea"),
    mutationFn: async (payload: { slotId: string; ideaId: string; instruction: string }) => {
      const res = await axiosInstance.post<SlotResponse>(
        `/content-calendar/plan-slots/${payload.slotId}/ideas/${payload.ideaId}/modify`,
        { instruction: payload.instruction },
      );
      return res.data.slot;
    },
    onMutate: async ({ slotId, ideaId }): Promise<IdeaUndo> => {
      await cancelPlanFetches(qc, planId);
      const status = readIdea(qc, planId, slotId, ideaId)?.status;
      patchIdea(qc, planId, slotId, ideaId, (idea) => ({ ...idea, status: "WORKING", updatedAt: new Date().toISOString() }));
      return { slotId, ideaId, status };
    },
    onSettled: settle,
    onError: revertIdea("Could not modify the idea"),
  });

  /**
   * Operator inline edits — the channels chips and the duration field. The
   * server re-applies its own rules (channel enum, a static never keeps a
   * duration), so the response is the truth even when it differs from the click.
   */
  const updateIdea = useMutation({
    mutationKey: writeKey("updateIdea"),
    mutationFn: async (payload: { slotId: string; ideaId: string; fields: IdeaEditInput }) => {
      const res = await axiosInstance.patch<SlotResponse>(
        `/content-calendar/plan-slots/${payload.slotId}/ideas/${payload.ideaId}`,
        payload.fields,
      );
      return res.data.slot;
    },
    onMutate: async ({ slotId, ideaId, fields }) => {
      await cancelPlanFetches(qc, planId);
      const slot = readSlot(qc, planId, slotId);
      patchIdea(qc, planId, slotId, ideaId, (idea) => ({ ...idea, ...fields }));
      return { slot } as SlotUndo;
    },
    onSettled: settle,
    onError: revertSlot("Could not save the idea"),
  });

  /** Keep the concept copy intact and replace only its proposal media. */
  const replaceIdeaMedia = useMutation({
    mutationKey: writeKey("replaceIdeaMedia"),
    /** `preview`: the picked media as the slide shows it, painted before the server answers. */
    mutationFn: async (payload: { slotId: string; ideaId: string; media: IdeaMediaPick; preview?: IdeaReference | null }) => {
      const res = await axiosInstance.patch<SlotResponse>(
        `/content-calendar/plan-slots/${payload.slotId}/ideas/${payload.ideaId}/media`,
        payload.media,
      );
      return res.data.slot;
    },
    onMutate: async ({ slotId, ideaId, media, preview }): Promise<SlotUndo> => {
      await cancelPlanFetches(qc, planId);
      const slot = readSlot(qc, planId, slotId);
      if (media.kind === "none" || preview !== undefined) {
        patchSlot(qc, planId, slotId, (s) => ({
          ...s,
          ideas: (s.ideas ?? []).map((i) => (i.id === ideaId ? { ...i, reference: media.kind === "none" ? null : (preview ?? i.reference) } : i)),
        }));
      }
      return { slot };
    },
    onSettled: settle,
    onSuccess: () => {
      queue.add({
        title: "Media changed",
        description: "The idea, its hooks and why it works stay as they are.",
        variant: "success",
      });
    },
    onError: revertSlot("Could not change the media"),
  });

  /** Choosing is internal. Only an explicit send publishes an immutable client version. */
  const acceptIdea = useMutation({
    mutationKey: writeKey("acceptIdea"),
    /** `pickedTitle` names the pick in the toast ("Idea 2 picked."), when the caller knows it. */
    mutationFn: async (payload: { slotId: string; ideaId: string; pickedTitle?: string }) => {
      const res = await axiosInstance.post<AcceptIdeaResult>(
        `/content-calendar/plan-slots/${payload.slotId}/ideas/${payload.ideaId}/accept`,
      );
      return res.data;
    },
    onMutate: async ({ slotId, ideaId }): Promise<SlotUndo> => {
      await cancelPlanFetches(qc, planId);
      const slot = readSlot(qc, planId, slotId);
      patchSlot(qc, planId, slotId, (s) => ({
        ...s,
        status: "SIGNED_OFF",
        ideas: (s.ideas ?? []).map((i) => ({ ...i, status: i.id === ideaId ? "ACCEPTED" : "PROPOSED" })),
      }));
      return { slot };
    },
    onSettled: settleResult,
    onSuccess: (result, variables) => {
      void qc.invalidateQueries({ queryKey: conceptVersionsKey(result.slot.id) });
      // Creo starts the brief the moment an idea is picked (Figma 08, idea 2 picked, with Undo).
      void qc.invalidateQueries({ queryKey: planBriefsKey(planId) });
      queue.add({
        title: variables.pickedTitle ?? "Idea picked. Creo writes the brief.",
        description: "Undo brings the three ideas back.",
        variant: "success",
        action: { label: "Undo", onPress: () => unpickIdea.mutate({ slotId: variables.slotId }) },
        countdown: 8,
      });
    },
    onError: revertSlot("Could not approve the idea"),
  });

  /** Undo a pick before anything went to the client: back to the ideas, the pick's brief dropped. */
  const unpickIdea = useMutation({
    mutationKey: writeKey("unpickIdea"),
    mutationFn: async (payload: { slotId: string }) => {
      const res = await axiosInstance.post<{ slot: CalendarPlanSlot }>(
        `/content-calendar/plan-slots/${payload.slotId}/unpick`,
      );
      return res.data;
    },
    onMutate: async ({ slotId }): Promise<SlotUndo> => {
      await cancelPlanFetches(qc, planId);
      const slot = readSlot(qc, planId, slotId);
      patchSlot(qc, planId, slotId, (s) => ({
        ...s,
        status: "IDEATION",
        ideas: (s.ideas ?? []).map((i) => (i.status === "ACCEPTED" ? { ...i, status: "PROPOSED" } : i)),
      }));
      return { slot };
    },
    onSettled: settleResult,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: planBriefsKey(planId) });
    },
    onError: revertSlot("Could not undo the pick"),
  });

  /**
   * The team writes the concept themselves: on a post with no ideas, or beside
   * the three (Write your own idea). Either way it is the pick: the post lands
   * SIGNED_OFF and Creo writes the brief, as for a picked idea. `reference` is
   * the first reference's snapshot for the optimistic slide, when the caller has it.
   */
  const createManualIdea = useMutation({
    mutationKey: writeKey("createManualIdea"),
    mutationFn: async (payload: { slotId: string; fields: ManualIdeaInput; reference?: IdeaReference | null }) => {
      const res = await axiosInstance.post<AcceptIdeaResult>(
        `/content-calendar/plan-slots/${payload.slotId}/ideas/manual`,
        payload.fields,
      );
      return res.data;
    },
    onMutate: async ({ slotId, fields, reference }): Promise<SlotUndo> => {
      await cancelPlanFetches(qc, planId);
      const slot = readSlot(qc, planId, slotId);
      const now = new Date().toISOString();
      patchSlot(qc, planId, slotId, (s) => ({
        ...s,
        status: "SIGNED_OFF",
        ideaError: null,
        ideas: [
          // The three stay as proposals beside yours.
          ...(s.ideas ?? []).map((i) => ({ ...i, status: i.status === "ACCEPTED" ? ("PROPOSED" as const) : i.status })),
          {
            id: `manual-${slotId}`,
            slotId,
            title: fields.title,
            synopsis: fields.synopsis,
            hooks: fields.hooks,
            objectiveClaim: fields.objectiveClaim ?? "",
            channels: fields.channels ?? [],
            aspectRatio: s.aspectRatio,
            durationSeconds: fields.durationSeconds ?? null,
            reference: reference ?? null,
            sources: [],
            status: "ACCEPTED",
            order: (s.ideas ?? []).reduce((max, i) => Math.max(max, (i.order ?? 0) + 1), 0),
            decidedByUserId: null,
            decidedAt: now,
            createdAt: now,
            updatedAt: now,
          },
        ],
      }));
      return { slot };
    },
    onSettled: settleResult,
    onSuccess: (result, variables) => {
      void qc.invalidateQueries({ queryKey: conceptVersionsKey(result.slot.id) });
      void qc.invalidateQueries({ queryKey: planBriefsKey(planId) });
      queue.add({
        title: "Your idea is picked. Creo writes the brief.",
        description: "Undo brings the ideas back.",
        variant: "success",
        action: { label: "Undo", onPress: () => unpickIdea.mutate({ slotId: variables.slotId }) },
        countdown: 8,
      });
    },
    onError: revertSlot("Could not save your idea"),
  });

  return {
    generateIdeas,
    createManualIdea,
    selectDeclinedIdea,
    regenerateIdea,
    modifyIdea,
    updateIdea,
    replaceIdeaMedia,
    acceptIdea,
    unpickIdea,
    cancelIdeaRework,
  };
}

/**
 * Approve a post WITHOUT the client — the team answering on their behalf. One
 * answer covers the idea and its creative brief, so the brief must be written
 * (the server refuses otherwise) and is accepted in the same call.
 *
 * Takes a post from either side of the client stop: from SIGNED_OFF it simply
 * skips the review, and from IN_CLIENT_REVIEW the server pulls it off the
 * client's board first. A client who answered inside that race window wins, and
 * the call comes back 409 — hence the resync, not just the rollback.
 */
export function useTeamApproveSlot(planId: string | null) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: [...planWriteKey(planId), "teamApprove"],
    mutationFn: async (slotId: string) => {
      const res = await axiosInstance.post<AcceptIdeaResult>(
        `/content-calendar/plan-slots/${slotId}/approve`,
      );
      return res.data;
    },
    onMutate: async (slotId): Promise<SlotUndo & { undoBrief?: () => void }> => {
      await cancelPlanFetches(qc, planId);
      const slot = readSlot(qc, planId, slotId);
      patchSlot(qc, planId, slotId, (s) => ({
        ...s,
        status: "CLIENT_APPROVED",
        approvalSource: "TEAM",
        updatedAt: new Date().toISOString(),
      }));
      const undoBrief = await acceptBriefWithPost(qc, planId, slotId);
      return { slot, undoBrief };
    },
    onSuccess: (_result, slotId) => {
      // The idea and its brief are one answer: the server approved the brief with it.
      qc.invalidateQueries({ queryKey: planBriefsKey(planId) });
      qc.invalidateQueries({ queryKey: slotBriefKey(slotId) });
      queue.add({
        title: "Approved on record",
        description: "The idea and its brief are approved. Add the files next.",
        variant: "success",
      });
    },
    onError: (err, _slotId, undo) => {
      if (undo?.slot) putSlot(qc, planId, undo.slot);
      undo?.undoBrief?.();
      queue.add({
        title: "Could not approve this post",
        description: errorMessage(err, "Please try again."),
        variant: "error",
      });
    },
    onSettled: (result, err) =>
      settlePlanWrite(qc, planId, err ? { refetch: true } : { slot: result?.slot }),
  });
}
